import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';
import { getApiConfig } from '@/config/api';
import i18n from '@/i18n';

/**
 * Language sent with every API request so the server can localize generated content
 * (for example report exports). Normalized to the supported codes "id" or "en".
 * @returns "en" when the selected app language is English, otherwise "id"
 */
const getRequestLanguage = (): 'id' | 'en' =>
  (i18n.language || 'id').toLowerCase().startsWith('en') ? 'en' : 'id';

// Create a dynamic base query that always uses the current hostname
const dynamicBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  // Get fresh API configuration on every request
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      // Get the token from the state
      let token = (getState() as RootState).auth.token;

      // Fallback to localStorage if token not in state
      if (!token) {
        token = localStorage.getItem('token');
      }

      // If we have a token, add it to the headers
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      // Skip ngrok browser warning for API requests
      headers.set('ngrok-skip-browser-warning', 'true');

      // Tell the server which app language is selected
      headers.set('accept-language', getRequestLanguage());

      // Attach store ID for subscription validation
      const storeId = localStorage.getItem('selectedStoreId');
      if (storeId) {
        headers.set('x-store-id', storeId);
      }

      // Set content-type to application/json by default, but only if body is not FormData
      if (!headers.has('content-type')) {
        // Check if this is a FormData request by looking at the request body
        const isFormData = args && typeof args === 'object' && 'body' in args &&
          args.body instanceof FormData;

        if (!isFormData) {
          headers.set('content-type', 'application/json');
        }
        // For FormData, let the browser set the content-type with boundary
      }

      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

// Define a service using a base URL and expected endpoints
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    'Auth',
    'User',
    'Category',
    'Subcategory',
    'Item',
    'ItemStock',
    'ItemUnit',
    'Customer',
    'Transaction',
    'TransactionItem',
    'TransactionStats',
    'Payment',
    'Expense',
    'ExpenseCategory',
    'ExpenseSubcategory',
    'Budget',
    'ExpenseStats',
    'Settings',
    'Report',
    'AuditLog',
    'Audit',
    'AuditSummary',
    'AuditActions',
    'EntityTypes',
    'OutletCash',
    'OutletCashTransaction',
    'OutletCashAnalytics',
    'CashDrawer',
    'InventoryStats',
    'Store',
    'Outlet',
    'StockTransfer',
    'SalesAnalytics',
    'StoreCash',
    'StoreCashTransaction',
    'StoreCashAnalytics',
    'OutletDrawer',
    'Promotion',
    'RoleTemplate',
    'DebitCardAccount',
    'QrisCode',
  ],
  endpoints: () => ({}),
});

// Export the dynamic base query for use in separate API instances
export { dynamicBaseQuery };

// Backward compatibility export
export const api = baseApi;

